import { Css, Tooltip, Typography, useTestIds, Xss } from "@homebound/beam";
import { ReactNode } from "react";
import { Icon } from "src/components";
import { isDefined } from "src/utils";
import { centsToDollars, formatNumberToString, isNegative, roundCentsToDollars } from "src/utils/numbers";

/**
 * Reuseability of this component hasn't yet been decided upon by design team
 * Standardized category of metric sizes/subtitles are still wip
 */
type HeroMetricProps = {
  value: number | null | undefined;
  subTitle1: ReactNode;
  subTitle2?: ReactNode;
  isPercent?: boolean;
  /** Display the metric value as green based on if the value is negative or positive*/
  greenIf?: "positive" | "negative";
  size?: HeroSize;
  colorXss?: Xss<"color">;
  toolTipText?: ReactNode;
  rounded?: boolean;
  /** Display the plus prefix for positive numberes */
  displayDirection?: boolean;
  /** If false minimal decimal is 0, max decimal is 2 (default = max,min decimal is 2 ) */
  trim?: boolean;
};

export function HeroMetric({
  value,
  subTitle1,
  subTitle2,
  isPercent,
  greenIf,
  size,
  colorXss,
  toolTipText,
  rounded = true,
  displayDirection,
  trim = true,
}: HeroMetricProps) {
  const tid = useTestIds({});
  const colorStyle = colorXss
    ? colorXss
    : greenIf === "positive"
      ? Css.if(isDefined(value) && isNegative(value)).red600.else.green800.$
      : greenIf === "negative"
        ? Css.if(isDefined(value) && (isNegative(value) || value === 0)).green800.else.red600.$
        : undefined;
  const prefix = isDefined(value) && (isNegative(value) ? "-$" : displayDirection && !isNegative(value) ? "+$" : "$");
  const formatted = formatNumberToString(
    rounded ? roundCentsToDollars(Math.abs(value || 0)) : centsToDollars(Math.abs(value || 0)),
    trim,
  );

  // If the value is 0, we want to show N/A if showNA is true otherwise show formatted value
  const formattedValue = !isDefined(value) ? "N/A" : formatted;

  return (
    <div css={Css.df.$}>
      {/** Subscript style price prefix i.e. -$ or +$ */}
      {!isPercent && isDefined(value) && (
        <div css={{ ...colorStyle, ...Css.asfs[size ? prefixSizeToType[size] : "smSb"].$ }}>{prefix}</div>
      )}
      <div css={Css.df.fdc.$} {...tid.subTitles}>
        <div
          {...tid.metricValue}
          css={{
            ...(isDefined(size)
              ? { ...colorStyle, ...Css[metricSizeToType[size]].$ }
              : { ...colorStyle, ...Css.xl2.$ }),
          }}
        >
          {isPercent ? (
            <div>
              <span>{isDefined(value) && formatNumberToString(value, true)}</span>
              {/** Subscript style percentage sign*/}
              <span css={Css.mdSb.$}>%</span>
            </div>
          ) : (
            formattedValue
          )}
        </div>
        <div css={Css.df.fdc.$}>
          <div css={Css.df.fdr.aic.gap1.$}>
            <div css={{ ...(isDefined(size) ? Css[subTitleSizeToType[size]].$ : Css.smSb.$) }}>{subTitle1}</div>
            {toolTipText && (
              <Tooltip placement="top" title={toolTipText}>
                <Icon icon="infoCircle" pxSize={12} />
              </Tooltip>
            )}
          </div>
          <div css={{ ...(isDefined(size) ? Css[subTitleSizeToType[size]].$ : Css.xs2.$) }}>{subTitle2}</div>
        </div>
      </div>
    </div>
  );
}

export type HeroSize = "xs" | "sm" | "md" | "lg";

const metricSizeToType: Record<HeroSize, Typography> = {
  xs: "lg",
  sm: "xl",
  md: "xl2",
  lg: "xl2",
};

const subTitleSizeToType: Record<HeroSize, Typography> = {
  xs: "xs2",
  sm: "smSb",
  md: "md",
  lg: "mdSb",
};

const prefixSizeToType: Record<HeroSize, Typography> = {
  xs: "smSb",
  sm: "md",
  md: "mdSb",
  lg: "lg",
};
